import type { ComponentType, ReactNode } from "react";
import type { BackgroundContext } from "./background.ts";
import type { Needs } from "./can.ts";
import type { NowRange } from "@signet/kernel/now-range.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The web half of a module.

   Pages, the nav entry, palette commands, home-screen cards and background
   work, each with the permission it needs. The app hides what the person cannot use; the server
   refuses it regardless.
   ────────────────────────────────────────────────────────────────────────── */

export type NowCardData = { id: string; module: string; title: string; kind: string; data: any; priority?: number };

export type WebRoute = {
  /** Absolute path, starting with the module's own prefix: "/research/niches". */
  path: string;
  component: ComponentType;
  /** One permission, or several of which any one is enough. */
  needs: Needs;
  /** Turns raw search params into typed state. Filters and selection live in the URL. */
  validateSearch?: (raw: Record<string, unknown>) => Record<string, unknown>;
};

/**
 * Work that runs for the whole app rather than for one page, such as desktop
 * notifications about your own work. The shell starts it once the person is
 * signed in and holds `needs`, and calls the returned function to stop it on
 * sign-out or when they lose the permission. It never runs on the sign-in
 * screen. `start` may be called again after a stop (React's strict mode does
 * exactly that in development), so it must not rely on running once per page
 * load.
 */
export type WebBackground = {
  needs: string;
  start: (ctx: BackgroundContext) => () => void;
};

export type WebNav =
  | { label: string; icon: ComponentType<{ size?: number }>; to: string; needs: Needs }
  /**
   * A place that is promised but not built: shown to everyone signed in, in
   * its registry position, with a Coming soon tag and no link. It has no route
   * and needs no permission, so there is nothing to open and nothing to
   * refuse. The timesheet held one until 2026-10-14; none does now.
   */
  | { label: string; icon: ComponentType<{ size?: number }>; soon: true };

/**
 * Several modules' places under one entry in the bar, which opens to list
 * them. The group takes the position of its first module in the registry and
 * is left out when the person can open none of them. Modules do not know
 * which group they are in; the web registry says, so regrouping is one line.
 */
export type NavGroup = {
  id: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** Module ids, listed in the menu in registry order. */
  modules: string[];
};

/**
 * The page an account this module binds sees (a creator's own sign-in), for
 * modules whose server half declares `boundRoutes`. It renders in its own
 * minimal frame, never inside the staff shell: such an account has no nav,
 * no palette, no assistant and no settings, and every staff route of the API
 * answers it 404 anyway.
 */
export type WebBoundPage = {
  /** Absolute path of the bound account's pages, outside every staff prefix: "/creator". */
  path: string;
  /** A single page at `path`, for a module whose bound accounts see one page and no frame of its own. */
  component?: ComponentType;
  /**
   * The frame around the pages modules add with `portal` (a creator's
   * portal: sidebar, photo, colours). It gets the pages for its nav and
   * renders the current one with <Outlet />.
   */
  frame?: ComponentType<PortalFrameProps>;
  /**
   * Colleagues can open these pages as a given record from the staff app,
   * at /preview/<module>/<record>, when the server half declares `preview`.
   */
  preview?: boolean;
};

/**
 * What a portal frame is given. `api` is where its pages' routes are:
 * "/api/bound" for the account itself, "/api/preview/<module>/<record>" in a
 * colleague's preview, which also carries `preview` (who, and where "Back to
 * Signet" goes). The frame hands both to its pages (kit PortalModeProvider).
 */
export type PortalFrameProps = { pages: PortalNavItem[]; tour: PortalTourStep[]; api: string; preview: { record: string; exit: string } | null };

/** One page of a bound account's portal, from any module that serves those accounts. */
export type WebPortalPage = {
  /** Under the binding module's `bound.path`: "" is its home, "saved" is /creator/saved. */
  path: string;
  label: string;
  /**
   * The word under its icon in a phone's bar along the bottom, when `label` is longer than about seven
   * letters: six full labels ran the bar off the screen and hid Settings (2026-10-27).
   */
  short?: string;
  icon: ComponentType<{ size?: number }>;
  /** Place in the portal's nav, lowest first, across every module. */
  order: number;
  component: ComponentType;
};

/** What the frame's nav gets: each page's full path, and its own (`path`, "" for home). */
export type PortalNavItem = { to: string; path: string; label: string; short?: string; icon: ComponentType<{ size?: number }>; order: number };

/**
 * One step of the welcome tour a bound account's portal plays the first time
 * they sign in (Jay, 2026-10-29), from any module that serves those accounts,
 * in `order` across them all. The frame plays it (kit PortalTourProvider).
 */
export type WebPortalTourStep = {
  id: string;
  order: number;
  /** The portal page it shows, as a page's `path` ("" is home). Left out, the tour stays where it is. */
  page?: string;
  /**
   * The `data-tour` name of what it explains; a list tries each in turn (the
   * first video, else the list it would be in). Left out, the card sits in
   * the middle of the screen.
   */
  target?: string | string[];
  /** What the step asks its page to show, read with the kit's useTourValue: { "ideas.tab": "saved" }. */
  set?: Record<string, string>;
  /** Room round the target inside the spotlight, in pixels. */
  pad?: number;
  /** A selector for the one thing to tap, ringed. */
  tap?: string;
  /** A selector for what may be tapped during the step; everything else is held still. */
  tryIt?: string;
  /** Tapping `tryIt` moves the tour on: at once, or "after" the tap has done its own thing (a burst, a sheet closing). */
  advance?: boolean | "after";
  /** A small moving picture, shown when the card sits in the middle (no target, or none found). */
  art?: ComponentType;
  /** The heading; a function gets the person's first name. */
  title: string | ((first: string) => string);
  body: ReactNode;
};

/** A tour step as the frame gets it: `to` is the full path of its page. */
export type PortalTourStep = Omit<WebPortalTourStep, "page"> & { to?: string };

/**
 * A section of a bound account's settings on People, from a module that
 * serves those accounts: Ideas adds a creator's niches to their login's
 * drawer. `record` is the account's record (the creator's name).
 */
export type WebStaffSection = {
  id: string;
  label: string;
  needs: string;
  order: number;
  component: ComponentType<{ record: string }>;
};

/**
 * Pages and People sections for the accounts another module binds. `for` is
 * that module's id; the server half declares `servesBound` with the same id.
 * A new creator feature is a module with one of these: the portal's frame
 * and nav pick it up without changing.
 */
export type WebPortal = { for: string; pages?: WebPortalPage[]; tour?: WebPortalTourStep[]; staffSections?: WebStaffSection[] };

/**
 * A tab on People beside Staff, such as Creators' list of creators and their
 * logins. It gets the staff sections other modules add for the accounts
 * this module binds.
 */
export type WebPeopleTab = {
  id: string;
  label: string;
  needs: string;
  order: number;
  component: ComponentType<{ sections: WebStaffSection[] }>;
};

/** What /api/bound answers for a bound account. `modules` is every module with pages for it. */
export type BoundMe = { module: string; username: string; modules: string[] };

/**
 * A figure in the row across the top of Now (visitors, revenue and so on).
 * It fetches its own numbers for the range the page is set to, so a module
 * adds one without the page knowing what it counts. `order` places it in the
 * row, lowest first, across every module.
 */
export type WebNowStat = { id: string; needs: string; order: number; component: ComponentType<{ range: NowRange }> };

/** A larger panel under the figures, beside Needs attention. Same rules. */
export type WebNowPanel = {
  id: string;
  needs: string;
  /** Place on Now, lowest first, read left to right in two columns. */
  order: number;
  /** Takes the whole row rather than half of it. */
  wide?: boolean;
  component: ComponentType<{ range: NowRange }>;
};

/**
 * A strip at the very top of Now, above the greeting and everything else
 * (Jay, 2026-10-21: what runs out in the next few days). It draws nothing
 * when it has nothing to say, so Now opens as it always did.
 */
export type WebNowTop = { id: string; needs: string; order: number; component: ComponentType };

/**
 * A section of the API/AI Usage page (/usage) for an outside service this
 * module pays for, such as Research's HikerAPI. The page shows every section
 * the person can see, under a heading with `label`, lowest `order` first, and
 * is open to anyone who can see at least one. Lazy load the component
 * (lazyPage) so the module's usage code is fetched only on that page.
 */
export type WebUsagePanel = {
  id: string;
  label: string;
  needs: string;
  order: number;
  /** The whole card, drawn with UsageCard so it matches the live providers' cards beside it. */
  component: ComponentType;
};

/**
 * One of this module's pages about a single creator, offered by the desktop
 * quick bar as "<creator> <label>" (Jay, 2026-10-01: "lexi links", "lexi
 * uploads"). `creator` is the name as the Creators roster has it. `to` may
 * look something up first, for a module that addresses creators by its own
 * id; it should fall back to the module's creator list rather than throw.
 */
export type WebCreatorPage = {
  id: string;
  label: string;
  needs: string;
  /** Lowest first, after the creator's own page. */
  order: number;
  keywords?: string;
  to: (creator: string) => string | Promise<string>;
};

/**
 * A row in the hero of a creator's page on Creators, from another module that
 * keeps something about each creator: Ideas puts their niches there, under
 * the timezone and above the social rail (Jay, 2026-10-15). `creator` is the
 * name as the roster has it. It is drawn inside the Creators page, so its
 * classes carry its own module's prefix like anywhere else.
 */
export type WebCreatorHeroRow = {
  id: string;
  needs: string;
  /** Lowest first, when more than one module adds a row. */
  order: number;
  component: ComponentType<{ creator: string }>;
};

/**
 * A tab of its own on a creator's page on Creators, beside Overview (the
 * page as it always was), from another module that keeps something about
 * each creator: Main Accounts' Trial Reels (Jay, 2026-10-22). Drawn inside
 * the Creators page, so it uses the page's own classes or its module's prefix.
 */
export type WebCreatorTab = {
  id: string;
  label: string;
  needs: string;
  /** Lowest first, after Overview. */
  order: number;
  component: ComponentType<{ creator: string }>;
};

export type WebModule = {
  id: string;
  nav?: WebNav;
  routes: WebRoute[];
  commands?: { id: string; label: string; to: string; needs: string; hint?: string; keywords?: string }[];
  creatorPages?: WebCreatorPage[];
  creatorHeroRows?: WebCreatorHeroRow[];
  creatorTabs?: WebCreatorTab[];
  /** Components for this module's home cards, by card kind. */
  /**
   * Card kinds that sit in their own column beside Needs attention rather
   * than inside it (Jay, 2026-10-08: stalled onboarding on the left,
   * everything else to act on on the right).
   */
  nowSideCards?: string[];
  nowCards?: Record<string, ComponentType<{ card: NowCardData }>>;
  nowStats?: WebNowStat[];
  nowPanels?: WebNowPanel[];
  nowTop?: WebNowTop[];
  usagePanels?: WebUsagePanel[];
  background?: WebBackground;
  bound?: WebBoundPage;
  portal?: WebPortal;
  peopleTabs?: WebPeopleTab[];
};

export function defineWebModule(module: WebModule): WebModule {
  return module;
}
